import React, { useEffect, useState, useContext } from 'react';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import LoadingOverlay from './LoadingOverlay';
import { UserContext } from './UserContext';
import { API_ENDPOINTS } from '../config/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const SalesPerformanceComparison = () => {
    const context = useContext(UserContext);
    const { user, restaurant } = context || {};
    const restaurantId = restaurant?._id || user?.id;
    const [chartData, setChartData] = useState({ labels: [], datasets: [] });
    const [groupBy, setGroupBy] = useState('day'); // "day", "week", or "month"
    const [detailedOrders, setDetailedOrders] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState('');
    const [showDetails, setShowDetails] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (restaurantId) {
            fetchPerformanceData();
        }
    }, [groupBy, restaurantId]);

    const fetchPerformanceData = async () => {
        if (!restaurantId) return;
        try {
            setIsLoading(true);
            const response = await axios.get(API_ENDPOINTS.ANALYTICS_SALES_PERFORMANCE(restaurantId, groupBy));
            const data = response.data;
            const labels = data.map(item => item._id);
            const orderCounts = data.map(item => item.orderCount);
            const avgOrderValues = data.map(item => item.avgOrderValue);

            setChartData({
                labels,
                datasets: [
                    {
                        type: 'bar',
                        label: 'Order count',
                        data: orderCounts,
                        backgroundColor: 'rgba(59, 130, 246, 0.7)', // blue
                        borderColor: 'rgba(37, 99, 235, 1)',
                        borderWidth: 1,
                        borderRadius: 6,
                        borderSkipped: false,
                        maxBarThickness: 32,
                    },
                    {
                        type: 'line',
                        label: 'Avg order value (₹)',
                        data: avgOrderValues,
                        borderColor: 'rgba(249, 115, 22, 1)', // orange
                        borderWidth: 2,
                        fill: false,
                        tension: 0.35,
                        pointRadius: 3,
                        pointBackgroundColor: 'rgba(249, 115, 22, 1)',
                        yAxisID: 'y1',
                    },
                ],
            });
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error('Error fetching performance data:', error);
        }
    };

    // onClick handler for the chart to trigger drill-down
    const onChartClick = async (event, elements) => {
        setIsLoading(true);
        if (!elements.length) return;
        const index = elements[0].index;
        const clickedLabel = chartData.labels[index];
        setSelectedPeriod(clickedLabel);

        // Fetch detailed orders for this period using our new endpoint
        try {
            const response = await axios.get(`${API_ENDPOINTS.ANALYTICS_ORDERS_BY_PERIOD}?period=${clickedLabel}&groupBy=${groupBy}`);
            setDetailedOrders(response.data);
            setShowDetails(true);
        } catch (error) {
            console.error('Error fetching detailed orders:', error);
        }
        setIsLoading(false);
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: onChartClick,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                position: 'bottom',
                labels: { boxWidth: 8, font: { size: 10 } },
            },
            title: { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y || 0;
                        const label = context.dataset.label || '';
                        if (label.toLowerCase().includes('value')) {
                            return `${label}: ₹${value.toLocaleString('en-IN')}`;
                        }
                        return `${label}: ${value.toLocaleString('en-IN')}`;
                    },
                },
            },
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: { display: true, text: 'Order count' },
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                title: { display: true, text: 'Avg order value (₹)' },
                grid: { drawOnChartArea: false },
            },
        },
    };

    return (
        <>
            {isLoading && <LoadingOverlay />}
            <div className="p-2 sm:p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Sales performance</p>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-stone-500 font-semibold uppercase">Group</span>
                            <select
                                value={groupBy}
                                onChange={(e) => setGroupBy(e.target.value)}
                                className="border border-stone-300 rounded-md px-2 py-1 text-[11px] bg-white"
                            >
                                <option value="day">Day</option>
                                <option value="week">Week</option>
                                <option value="month">Month</option>
                            </select>
                        </div>
                        <button
                            type="button"
                            onClick={() => fetchPerformanceData()}
                            className="px-2.5 py-1 text-[11px] font-medium rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50"
                        >
                            Refresh
                        </button>
                    </div>
                </div>
                <div className="mt-1" style={{ height: 220 }}>
                    <Bar data={chartData} options={options} />
                </div>

                {showDetails && (
                    <div className="mt-3">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-[12px] font-semibold text-stone-700">Detailed orders – {selectedPeriod}</h3>
                            <button
                                type="button"
                                onClick={() => setShowDetails(false)}
                                className="text-[11px] text-stone-500 hover:text-stone-700"
                            >
                                Close
                            </button>
                        </div>
                        <table className="min-w-full border text-[11px]">
                            <thead>
                                <tr>
                                    <th className="border p-2">Order Date</th>
                                    <th className="border p-2">Customer Name</th>
                                    <th className="border p-2">Mobile Number</th>
                                    <th className="border p-2">Total Amount (Rs.)</th>
                                    <th className="border p-2">Final Amount (Rs.)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detailedOrders.map((order) => (
                                    <tr key={order._id}>
                                        <td className="border p-2">{new Date(order.orderDate).toLocaleString()}</td>
                                        <td className="border p-2">{order.customerName}</td>
                                        <td className="border p-2">{order.mobileNumber}</td>
                                        <td className="border p-2">Rs. {order.totalAmount.toFixed(2)}</td>
                                        <td className="border p-2">Rs. {order.finalAmount.toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

export default SalesPerformanceComparison;
